import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { api } from "../lib/api";
import { setSeo } from "../lib/seo";

// Cornerstone introduction to the profession.
const FEATURED_SLUG = "how-to-build-trust-with-new-clients-in-estate-management-p9j9s";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "profession", label: "The Profession" },
  { key: "work", label: "The Work" },
  { key: "career", label: "Career" },
  { key: "systems", label: "Systems" },
];

const NOTES_COPY = "Thoughts, practical lessons and observations from the world of Estate Management — delivered occasionally.";

// Display-only editorial organization, derived from the article title. No content is invented.
const categorize = (p) => {
  const t = (p.title || "").toLowerCase();
  if (/(walkthrough|vendor|contractor|maintenance|calendar|inventor|system|checklist|preparedness)/.test(t)) return "systems";
  if (/(how to start|negotiate|your worth|mistakes|career|interview|position|shortage|transferable)/.test(t)) return "career";
  if (/(trust|emotional intelligence|quiet excellence|communication|beautiful home|aging in place|discretion|leadership|r[eé]sum|skill|dos and don)/.test(t)) return "work";
  return "profession";
};

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { setSeo({ title: "Journal — Notes on Private Service · Marella Bolognesi", description: "Observations, practical lessons and professional perspective on Estate Management, private service, careers and household systems — from Marella Bolognesi." }); }, []);
  useEffect(() => { api.get("/posts").then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);

  const featured = useMemo(() => posts && (posts.find((p) => p.slug === FEATURED_SLUG) || posts[0]), [posts]);

  const grid = useMemo(() => {
    if (!posts) return [];
    if (filter === "all") return posts.filter((p) => p !== featured);
    return posts.filter((p) => categorize(p) === filter);
  }, [posts, filter, featured]);

  return (
    <div data-testid="blog-page" className="pt-20">
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-20 lg:pt-28 pb-14">
        <FadeIn><MetaLabel>The Journal — Notes on Private Service</MetaLabel></FadeIn>
        <FadeIn delay={0.08}>
          <h1 className="mt-6 font-serif font-light text-ink text-5xl sm:text-6xl lg:text-[7vw] leading-[0.92] tracking-tight">
            Notes on <span className="italic">private service</span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.16}>
          <p className="mt-8 max-w-2xl text-lg font-light text-ink-soft leading-relaxed">
            Observations, practical lessons and professional perspective from the work of Estate Management and private service.
          </p>
        </FadeIn>
      </section>

      {!posts ? (
        <p className="px-6 lg:px-12 pb-32 text-sm text-ink-soft animate-pulse">Loading the journal…</p>
      ) : (
        <>
          {/* Editorial filters */}
          <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-12">
            <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-line pb-5">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`journal-filter-${c.key}`}
                  onClick={() => setFilter(c.key)}
                  className={`text-[11px] uppercase tracking-[0.2em] pb-1 border-b transition-colors ${filter === c.key ? "text-ink border-ink" : "text-ink-soft/60 border-transparent hover:text-ink"}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {filter === "all" && featured && (
            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-14">
              <Reveal>
                <Link to={`/blog/${featured.slug}`} data-testid="blog-lead" className="group grid lg:grid-cols-2 border border-line">
                  <div className="overflow-hidden aspect-[16/11] lg:aspect-auto">
                    <img src={featured.cover_url} alt={featured.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  </div>
                  <div className="p-8 lg:p-16 flex flex-col justify-center">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">{featured.date} · Start Here</p>
                    <h2 className="mt-5 font-serif text-3xl lg:text-5xl font-light text-ink leading-tight">{featured.title}</h2>
                    <p className="mt-5 text-lg font-light text-ink-soft leading-relaxed">{featured.excerpt}</p>
                    <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink">Read the article <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" /></span>
                  </div>
                </Link>
              </Reveal>
            </section>
          )}

          <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-28">
            {grid.length === 0 ? (
              <p data-testid="journal-empty" className="text-base font-light text-ink-soft">No articles in this section yet.</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                {grid.map((p, i) => (
                  <Reveal key={p.slug} delay={(i % 3) * 0.06} className="border border-line">
                    <Link to={`/blog/${p.slug}`} data-testid={`blog-post-${i}`} className="group block h-full">
                      <div className="overflow-hidden aspect-[16/10]">
                        <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                      </div>
                      <div className="p-8">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft">{p.date}</p>
                        <h3 className="mt-3 font-serif text-2xl font-normal text-ink leading-snug">{p.title}</h3>
                        <p className="mt-3 text-sm text-ink-soft font-light leading-relaxed">{p.excerpt}</p>
                        <span className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink">Read more <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" /></span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Notes from Il Salotto */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
          <Reveal className="lg:col-span-4"><MetaLabel>The Letter</MetaLabel></Reveal>
          <Reveal delay={0.08} className="lg:col-span-8">
            <NewsletterSignup source="journal" tone="light" withName copy={NOTES_COPY} />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
